import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useTwoFactorSettings } from '../hooks';

export const TwoFactorSettings = () => {
  const {
    loading,
    status,
    setup,
    code,
    setCode,
    recoveryCodes,
    manageCode,
    setManageCode,
    busy,
    startSetup,
    confirmSetup,
    cancelSetup,
    disable,
    regenerate,
  } = useTwoFactorSettings();

  const recoveryBlock = recoveryCodes.length > 0 && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Save your recovery codes
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        Each works once if you lose your authenticator. They will not be shown again.
      </Typography>
      <Grid container spacing={1}>
        {recoveryCodes.map((recoveryCode) => (
          <Grid item xs={6} key={recoveryCode}>
            <Chip label={recoveryCode} size="small" sx={{ width: '100%', fontFamily: 'monospace' }} />
          </Grid>
        ))}
      </Grid>
    </Alert>
  );

  if (loading) {
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle2">Two-Factor Authentication</Typography>
        <Skeleton variant="rounded" height={20} width="40%" />
        <Skeleton variant="rounded" height={40} />
      </Stack>
    );
  }

  if (setup) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Set up an authenticator app
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
            <QRCodeSVG value={setup.otpauthUri} size={160} includeMargin />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2">
              Scan this with Google Authenticator, Authy or 1Password, then enter the 6-digit code.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              No scanner? Enter this key manually:
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                wordBreak: 'break-all',
              }}
            >
              {setup.secret}
            </Box>
            <Box sx={{ mt: 2, maxWidth: 320 }}>
              <TextField
                label="Authentication code"
                value={code}
                fullWidth
                onChange={(e) => setCode(e.target.value.trim())}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
              <Button variant="contained" disabled={busy} onClick={() => void confirmSetup()}>
                {busy ? 'Verifying…' : 'Verify and enable'}
              </Button>
              <Button variant="text" onClick={cancelSetup}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </Stack>
        {recoveryBlock}
      </Box>
    );
  }

  if (status?.enrolled) {
    return (
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2">Two-Factor Authentication</Typography>
            <Typography variant="body2" color="success.main" fontWeight={600}>
              On · {status.recoveryCodesRemaining} recovery code{status.recoveryCodesRemaining === 1 ? '' : 's'} left
            </Typography>
          </Box>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-end' }} sx={{ mt: 2 }}>
          <Box sx={{ maxWidth: { sm: 320 }, width: '100%' }}>
            <TextField
              label="Enter a current code to make changes"
              value={manageCode}
              fullWidth
              onChange={(e) => setManageCode(e.target.value.trim())}
            />
          </Box>
          <Button variant="outlined" disabled={busy || !manageCode} onClick={() => void regenerate()}>
            Regenerate recovery codes
          </Button>
          <Button variant="text" color="error" disabled={busy || !manageCode} onClick={() => void disable()}>
            Turn off
          </Button>
        </Stack>
        {status.requiredByPolicy && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Your organization requires two-factor authentication.
          </Alert>
        )}
        {recoveryBlock}
      </Box>
    );
  }

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
    >
      <Box>
        <Typography variant="subtitle2">Two-Factor Authentication</Typography>
        <Typography variant="body2" color="text.secondary">
          {status?.requiredByPolicy ? 'Required by your organization.' : 'Add a second step to your sign-in.'}
        </Typography>
      </Box>
      <Button variant="contained" onClick={() => void startSetup()} sx={{ width: { xs: '100%', sm: 'auto' } }}>
        Set up
      </Button>
    </Stack>
  );
};
